<template>
  <teleport to="body">
    <div
      id="huddle_panel_expanded"
      v-if="isVisible && expanded"
      class="fixed inset-4 z-[1000] flex flex-col rounded-lg shadow-2xl overflow-hidden huddle_panel"
    >
      <div
        class="flex items-center justify-between px-4 py-2.5 select-none shrink-0 border_bottom huddle_bg"
      >
        <div class="flex items-center gap-3">
          <div class="flex items-center gap-1.5">
            <button class="w-3 h-3 rounded-full bg-red-500 hover:bg-red-600 transition-colors" title="Close" @click="handleLeave" />
            <button class="w-3 h-3 rounded-full bg-yellow-500 hover:bg-yellow-600 transition-colors" title="Minimize" @click="expanded = false" />
            <button class="w-3 h-3 rounded-full bg-green-500 hover:bg-green-600 transition-colors" title="Restore" @click="expanded = false" />
          </div>
        </div>
        <div class="flex items-center gap-2">
          <span class="font-medium text-sm flex items-center gap-1 text_color">
            Call with
            <ThemeImage v-if="huddleGroupType === 'channel' && isPrivateChannel" :icon="imageNames.LOCK_GREY" class="size-3.5 inline-block" alt="private" />
            <span v-else-if="huddleGroupType === 'channel'" class="secondary_color">#</span>
            {{ channelDisplayName }}
          </span>
          <span class="text-sm secondary_color">{{ participantCount }} {{ participantCount === 1 ? 'person' : 'people' }}</span>
        </div>
        <a-button
          class="secondary_color px-2"
          title="Restore"
          @click="expanded = false"
        >
          <ThemeImage
              class="size-5"
              :icon="imageNames.COLLAPSE_ICON"
              alt="collapse"
          />
        </a-button>
      </div>
      <div class="flex-1 min-h-0 huddle_bg">
        <ParticipantGrid />
      </div>
      <HuddleControls @open-add-people="showAddPeople = true" />
    </div>

    <transition
      enter-active-class="huddle_slide_up_enter"
      leave-active-class="huddle_slide_up_leave"
    >
      <div
        v-if="isAlive && !expanded"
        v-show="isVisible"
        id="huddle_panel"
        ref="panelRef"
        class="fixed z-[1000] flex flex-col rounded-lg shadow-2xl overflow-hidden huddle_panel"
        :style="panelStyle"
      >
        <div
          class="flex items-center justify-between px-4 py-2.5 select-none shrink-0 cursor-grab active:cursor-grabbing border_bottom huddle_bg"
          @mousedown.prevent="startDrag"
        >
          <div class="flex items-center gap-3">
            <div class="flex items-center gap-1.5" @mousedown.stop>
              <button class="w-3 h-3 rounded-full bg-red-500 hover:bg-red-600 transition-colors" title="Close" @click="handleLeave" />
              <button class="w-3 h-3 rounded-full bg-yellow-500 hover:bg-yellow-600 transition-colors" title="Minimize" @click="minimized = true" />
              <button class="w-3 h-3 rounded-full bg-green-500 hover:bg-green-600 transition-colors" title="Expand" @click="expanded = true" />
            </div>
          </div>
          <div class="flex items-center gap-2 min-w-0">
            <span class="font-medium text-sm flex items-center gap-1 truncate text_color">
              <ThemeImage v-if="huddleGroupType === 'channel' && isPrivateChannel" :icon="imageNames.LOCK_GREY" class="size-3.5 shrink-0" alt="private" />
              <span v-else-if="huddleGroupType === 'channel'" class="secondary_color shrink-0">#</span>
              <span class="truncate">{{ channelDisplayName }}</span>
            </span>
            <span class="text-sm secondary_color whitespace-nowrap">{{ participantCount }} {{ participantCount === 1 ? 'person' : 'people' }}</span>
          </div>
          <a-button
              class="secondary_color px-2"
              title="Expand"
              @click="expanded = true"
          >
            <ThemeImage
                class="size-5"
                :icon="imageNames.EXPAND_ICON"
                alt="collapse"
            />
          </a-button>
        </div>

        <div class="flex-1 min-h-0 huddle_bg">
          <ParticipantGrid />
        </div>

        <HuddleControls @open-add-people="showAddPeople = true" />

        <div
          class="absolute bottom-0 right-0 w-4 h-4 cursor-se-resize"
          @mousedown.prevent="startResize"
        >
          <ThemeImage :icon="imageNames.RESIZE_GRIP" class="size-4 absolute bottom-0 right-0 opacity-30" alt="resize" />
        </div>

        <div
          v-if="huddleStatus === 'connecting'"
          class="absolute inset-0 flex items-center justify-center rounded-lg huddle_panel"
        >
          <CustomLoader :size="24" :border-width="3" text="Connecting to call..." />
        </div>
      </div>
    </transition>

    <HuddleAddPeopleDialog v-if="showAddPeople" @close="showAddPeople = false" />
  </teleport>
</template>

<script setup>
import { computed, ref, watch, onMounted, onUnmounted } from "vue";
import { useStore } from "vuex";
import ParticipantGrid from "./ParticipantGrid.vue";
import HuddleControls from "./HuddleControls.vue";
import HuddleAddPeopleDialog from "./HuddleAddPeopleDialog.vue";
import CustomLoader from "@/src/components/CustomLoader.vue";
import ThemeImage from "@/src/components/ThemeImage.vue";
import { imageNames } from "@/src/constants/imageConstants.js";
import { chatTypes, chatRecipientTypes } from "@/src/constants/chatConstants.js";
import { renderEntity } from "@/src/utils/utils.js";
import eventBus from "@/src/utils/eventBus.js";
import { useHuddleControls } from "@/src/composables/useHuddleControls.js";
import {
  HUDDLE_PANEL_DEFAULT_WIDTH,
  HUDDLE_PANEL_DEFAULT_HEIGHT,
  HUDDLE_PANEL_MIN_WIDTH,
  HUDDLE_PANEL_MIN_HEIGHT,
} from "@/src/constants/huddleConstants.js";

const { handleLeave } = useHuddleControls();

const store = useStore();

const panelRef = ref(null);
const expanded = ref(false);
const minimized = computed({
  get: () => store.getters["huddles/panelMinimized"],
  set: (val) => store.commit("huddles/SET_PANEL_MINIMIZED", val),
});
const showAddPeople = ref(false);

// Position & size — restore from localStorage if available
const PANEL_STORAGE_KEY = "huddle_panel_layout";

const loadPanelLayout = () => {
  try {
    const saved = localStorage.getItem(PANEL_STORAGE_KEY);
    if (saved) return JSON.parse(saved);
  } catch { /* ignore */ }
  return null;
};

const savedLayout = loadPanelLayout();
const posX = ref(savedLayout?.x ?? -1);
const posY = ref(savedLayout?.y ?? -1);
const sizeW = ref(savedLayout?.w ?? HUDDLE_PANEL_DEFAULT_WIDTH);
const sizeH = ref(savedLayout?.h ?? HUDDLE_PANEL_DEFAULT_HEIGHT);

// Drag
let isDragging = false;
let dragOffsetX = 0;
let dragOffsetY = 0;

// Resize
let isResizing = false;
let resizeStartX = 0;
let resizeStartY = 0;
let resizeStartW = 0;
let resizeStartH = 0;

// Store
const activeHuddle = computed(() => store.getters["huddles/activeHuddle"]);
const participants = computed(() => store.getters["huddles/participants"]);
const isConnected = computed(() => store.getters["huddles/isConnected"]);
const connectionState = computed(() => store.getters["huddles/connectionState"]);

const allUsers = computed(() => store.getters["users/users"] || []);
const currentUserId = computed(() => store.getters["auth/userData"]?.id);

const huddleStatus = computed(() => connectionState.value);
const isAlive = computed(() => (isConnected.value || connectionState.value === "connecting") && !!activeHuddle.value);
const isVisible = computed(() => !minimized.value && isAlive.value);

const huddleGroup = computed(() => {
  if (!activeHuddle.value) return null;
  const groups = store.getters["chats/groups"] || [];
  return groups.find((g) => String(g.id) === String(activeHuddle.value.group_id)) || null;
});

const huddleGroupType = computed(() => {
  const group = huddleGroup.value;
  if (!group) return "unknown";
  if (group.type === chatTypes.DM || group.type === chatTypes.SELF_DM) return "dm";
  if (group.type === chatTypes.GROUP_DM) return "group_dm";
  if (group.type === chatTypes.PUBLIC || group.type === chatTypes.PRIVATE) return "channel";
  if (group.is_dm) return group.recipient_type === chatRecipientTypes.GROUP_DM ? "group_dm" : "dm";
  return "channel";
});

const isPrivateChannel = computed(() => {
  return huddleGroup.value?.type === chatTypes.PRIVATE;
});

const channelDisplayName = computed(() => {
  const group = huddleGroup.value;
  if (!group) return "Huddle";

  if (huddleGroupType.value === "dm") {
    // For DM, show the other user's name
    const members = participants.value;
    const otherUser = allUsers.value.find((u) => {
      const uid = Number(u.id);
      return uid !== Number(currentUserId.value) && members.some((m) => Number(m.user_id) === uid);
    });
    if (otherUser) return renderEntity(otherUser);
    // Fallback to group name
    return group.name || "Direct Message";
  }

  if (huddleGroupType.value === "group_dm") {
    return group.name || "Group DM";
  }

  // Channel group — just the name
  return group.name || "Huddle";
});

const participantCount = computed(() => participants.value.length);

// Center on first show if no saved position
watch(isVisible, (val) => {
  if (val && posX.value === -1) {
    posX.value = Math.max(0, (window.innerWidth - sizeW.value) / 2);
    posY.value = Math.max(0, 32);
    savePanelLayout();
  }
});

// Reset on huddle end — keep saved layout for next huddle
watch(activeHuddle, (val) => {
  if (!val) {
    const saved = loadPanelLayout();
    posX.value = saved?.x ?? -1;
    posY.value = saved?.y ?? -1;
    sizeW.value = saved?.w ?? HUDDLE_PANEL_DEFAULT_WIDTH;
    sizeH.value = saved?.h ?? HUDDLE_PANEL_DEFAULT_HEIGHT;
    expanded.value = false;
    minimized.value = false;
    showAddPeople.value = false;
  }
});

const panelStyle = computed(() => ({
  left: posX.value + "px",
  top: posY.value + "px",
  width: sizeW.value + "px",
  height: sizeH.value + "px",
}));

// Drag
const startDrag = (e) => {
  if (e.target.closest("button")) return;
  isDragging = true;
  dragOffsetX = e.clientX - posX.value;
  dragOffsetY = e.clientY - posY.value;
};

// Resize
const startResize = (e) => {
  isResizing = true;
  resizeStartX = e.clientX;
  resizeStartY = e.clientY;
  resizeStartW = sizeW.value;
  resizeStartH = sizeH.value;
};

const onMouseMove = (e) => {
  if (isDragging) {
    posX.value = Math.max(0, Math.min(e.clientX - dragOffsetX, window.innerWidth - 100));
    posY.value = Math.max(0, Math.min(e.clientY - dragOffsetY, window.innerHeight - 100));
  }
  if (isResizing) {
    sizeW.value = Math.max(HUDDLE_PANEL_MIN_WIDTH, Math.min(resizeStartW + (e.clientX - resizeStartX), window.innerWidth - posX.value));
    sizeH.value = Math.max(HUDDLE_PANEL_MIN_HEIGHT, Math.min(resizeStartH + (e.clientY - resizeStartY), window.innerHeight - posY.value));
  }
};

const savePanelLayout = () => {
  try {
    localStorage.setItem(PANEL_STORAGE_KEY, JSON.stringify({
      x: posX.value, y: posY.value, w: sizeW.value, h: sizeH.value,
    }));
  } catch { /* ignore */ }
};

const onMouseUp = () => {
  if (isDragging || isResizing) {
    savePanelLayout();
  }
  isDragging = false;
  isResizing = false;
};

const handleRestorePanel = () => {
  minimized.value = false;
};

onMounted(() => {
  window.addEventListener("mousemove", onMouseMove);
  window.addEventListener("mouseup", onMouseUp);
  eventBus.on("huddle-restore-panel", handleRestorePanel);
});

onUnmounted(() => {
  window.removeEventListener("mousemove", onMouseMove);
  window.removeEventListener("mouseup", onMouseUp);
  eventBus.off("huddle-restore-panel", handleRestorePanel);
});
</script>

<style scoped>
.huddle_slide_up_enter {
  animation: huddle_slide_up 0.3s ease-out forwards;
}

.huddle_slide_up_leave {
  animation: huddle_slide_down 0.2s ease-in forwards;
}

@keyframes huddle_slide_up {
  from { opacity: 0; transform: translateY(16px) scale(0.97); }
  to { opacity: 1; transform: translateY(0) scale(1); }
}

@keyframes huddle_slide_down {
  from { opacity: 1; transform: translateY(0) scale(1); }
  to { opacity: 0; transform: translateY(16px) scale(0.97); }
}

.huddle_panel {
  background: var(--background-color);
  border: 1px solid var(--white-opacity-10);
}

.huddle_bg {
  background: var(--background-color);
}
</style>
